import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Search, Zap, Recycle, Users } from "lucide-react";
import { motion } from "framer-motion";

const FeaturesSection = ({ variants }) => {
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  const featureCardVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { type: "spring", stiffness: 120, damping: 10 },
    },
  };

  const features = [
    {
      icon: Search,
      title: "AI-Powered Search",
      description:
        "Find the exact part you need quickly with our intelligent search assistant.",
    },
    {
      icon: Recycle,
      title: "Sustainable Choice",
      description:
        "Support the environment by choosing quality recycled and used parts.",
    },
    {
      icon: Zap,
      title: "Verified Businesses",
      description:
        "Connect with trusted sellers offering reliable auto components.",
    },
    {
      icon: Users,
      title: "Community Driven",
      description:
        "Join a growing marketplace focused on value and sustainability.",
    },
  ];

  return (
    <motion.section className="py-14 sm:py-16" variants={variants}>
      <motion.h2
        className="text-2xl sm:text-3xl font-bold text-center mb-10 sm:mb-12"
        variants={itemVariants}
      >
        Why Choose Parts Lokator?
      </motion.h2>
      {/* Updated Grid Layout: 2 columns on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center container mx-auto px-4">
        {/* Image Section (Takes up 1 column on large screens) */}
        <motion.div variants={itemVariants} className="flex justify-center">
          <Card className="overflow-hidden shadow-lg border border-primary/10 max-w-md w-full">
            {" "}
            {/* Constrained width */}
            <CardHeader>
              <CardTitle className="text-center text-xl font-semibold">
                AI-Powered Assistance
              </CardTitle>
              <CardDescription className="text-center text-muted-foreground">
                Our assistant helps you find parts faster.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <img
                src="/assets/AI-Assistant.jpg"
                alt="AI Robot assistant helping with auto part search on a mobile phone"
                className="w-full h-auto object-cover"
              />
            </CardContent>
          </Card>
        </motion.div>

        {/* Feature Cards Section (Takes up 1 column on large screens) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <motion.div key={index} variants={featureCardVariants}>
              <Card className="text-center h-full hover:shadow-lg hover:border-primary transition-all duration-300 ease-in-out group bg-card/80 backdrop-blur-sm border">
                <CardHeader className="items-center">
                  <div className="p-4 bg-primary/10 rounded-full mb-4 group-hover:bg-primary/20 transition-colors">
                    <feature.icon className="h-8 w-8 text-primary group-hover:scale-110 transition-transform" />
                  </div>
                  <CardTitle className="text-xl font-semibold">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{feature.description}</CardDescription>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default FeaturesSection;
